import React from 'react'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import type { ArgsProps } from 'antd/es/notification/interface'

import { NotificationActions } from './components/NotificationActions'
import {
  NOTIFICATION_CONFIG,
  NOTIFICATION_DURATION,
  NOTIFICATION_LIMIT
} from './constants'
import { getNotificationBridge } from './notificationBridge'
import type { NotificationStyles } from './notificationBridge'
import type {
  NotificationOptions,
  NotificationRequest,
  NotificationStatus
} from './types'
import { holdOnFocus, releaseOnBlur } from './utils'

const ICON_CLASS_MAP: Record<NotificationStatus, keyof NotificationStyles> = {
  info: 'iconInfo',
  success: 'iconSuccess',
  error: 'iconError',
  warning: 'iconWarning'
}

const visible: Array<{ key: React.Key; hasAction: boolean }> = []
const waiting: NotificationRequest[] = []
const suppressed = new Set<React.Key>()

let notificationKey = 0

const hasActions = (options: NotificationOptions) =>
  Boolean(options.primaryAction || options.secondaryAction)

const release = (key: React.Key) => {
  const index = visible.findIndex((item) => item.key === key)

  if (index === -1) {
    return
  }

  visible.splice(index, 1)
  flush()
}

const handleClose = (key: React.Key, onClose?: () => void) => {
  onClose?.()

  if (suppressed.delete(key)) {
    return
  }

  release(key)
}

const openNotice = (request: NotificationRequest) => {
  const bridge = getNotificationBridge()

  if (!bridge) {
    return
  }

  const { status, title, options, key } = request
  const withActions = hasActions(options)
  const showIcon = options.showIcon !== false
  const closable = options.closable !== false
  const closeLabel = typeof title === 'string' ? title : 'Close'

  const config: ArgsProps = {
    key,
    message: title,
    description: options.description,
    duration: withActions ? 0 : NOTIFICATION_DURATION,
    showProgress: !withActions,
    pauseOnHover: true,
    placement: 'topRight',
    role: withActions ? 'alert' : 'status',
    className: bridge.styles.notice,
    icon: showIcon ? (
      <FontAwesomeIcon
        icon={NOTIFICATION_CONFIG[status].icon}
        className={bridge.styles[ICON_CLASS_MAP[status]]}
        aria-hidden
      />
    ) : undefined,
    type: showIcon ? status : undefined,
    btn: withActions ? (
      <NotificationActions
        className={bridge.styles.actions}
        primaryAction={options.primaryAction}
        secondaryAction={options.secondaryAction}
        onAction={(action) => {
          action?.onClick?.()
          bridge.notification.destroy(key)
        }}
      />
    ) : undefined,
    closable: closable ? { 'aria-label': closeLabel } : false,
    closeIcon: closable ? <FontAwesomeIcon icon={faXmark} aria-hidden /> : null,
    onClose: () => handleClose(key, options.onClose),
    props: withActions
      ? undefined
      : {
          onFocus: holdOnFocus,
          onBlur: releaseOnBlur
        }
  }

  bridge.notification.open({
    ...config,
    styles: {
      wrapper: {
        background: 'transparent',
        boxShadow: 'none',
        borderRadius: 0
      }
    }
  } as ArgsProps)
}

const flush = () => {
  while (visible.length < NOTIFICATION_LIMIT && waiting.length > 0) {
    const next = waiting.shift()

    if (!next) {
      return
    }

    visible.push({ key: next.key, hasAction: hasActions(next.options) })
    openNotice(next)
  }
}

const show = (
  status: NotificationStatus,
  title: React.ReactNode,
  options: NotificationOptions = {}
) => {
  if (!getNotificationBridge()) {
    return
  }

  const key =
    options.key ?? `design-system-notification-${(notificationKey += 1)}`
  const request: NotificationRequest = { status, title, options, key }
  const withActions = hasActions(options)
  const visibleIndex = visible.findIndex((item) => item.key === key)

  if (visibleIndex >= 0) {
    visible[visibleIndex].hasAction = withActions
    openNotice(request)

    return
  }

  const waitingIndex = waiting.findIndex((item) => item.key === key)

  if (waitingIndex >= 0) {
    waiting[waitingIndex] = request

    return
  }

  if (visible.length >= NOTIFICATION_LIMIT) {
    const timelineIndex = visible.findIndex((item) => !item.hasAction)

    if (timelineIndex === -1) {
      waiting.push(request)

      return
    }

    const [removed] = visible.splice(timelineIndex, 1)

    suppressed.add(removed.key)
    getNotificationBridge()?.notification.destroy(removed.key)
  }

  visible.push({ key, hasAction: withActions })
  openNotice(request)
}

export function useNotification() {
  return {
    showInfo: (title: React.ReactNode, options?: NotificationOptions) =>
      show('info', title, options),
    showSuccess: (title: React.ReactNode, options?: NotificationOptions) =>
      show('success', title, options),
    showWarning: (title: React.ReactNode, options?: NotificationOptions) =>
      show('warning', title, options),
    showError: (title: React.ReactNode, options?: NotificationOptions) =>
      show('error', title, options)
  }
}
